import { Injectable, InjectionToken, Inject } from '@angular/core';
import { ConceptStatus, KnowledgeUserState } from '@dataclouder/ngx-knowledge'; // Assuming this path is correct
import Dexie, { Table } from 'dexie';

// Injection token for providing categories
export const CATEGORIES_TOKEN = new InjectionToken<string[]>('CATEGORIES_TOKEN');

@Injectable({
  providedIn: 'root',
})
export class KnowledgeStateTablesService extends Dexie {
  // Map to hold tables for each category
  private categoryTables: Map<string, Table<KnowledgeUserState, string>> = new Map();

  constructor(@Inject(CATEGORIES_TOKEN) categories: string[]) {
    super('KnowledgeStateDB'); // Database name

    const storesConfig: { [tableName: string]: string | null } = {};

    categories.forEach((category) => {
      storesConfig[category] = 'concept, conceptPath'; // Primary key 'concept', index 'conceptPath'
    });

    this.version(1).stores(storesConfig);

    // Initialize table references
    categories.forEach((category) => {
      this.categoryTables.set(category, this.table(category));
    });
    console.log('categoryTables', this.categoryTables);
  }

  /**
   * Retrieves the Dexie table for a given category.
   * @param category The category string.
   * @returns The Dexie Table instance.
   * @throws Error if the category is invalid or not configured.
   */
  private getTableByCategory<T>(category: T): Table<KnowledgeUserState, string> {
    console.log('getting table for category', category, this.categoryTables);

    const table = this.categoryTables.get(category as string);
    if (!table) {
      throw new Error(`KnowledgeDbService: No table found for category "${category}". Ensure this category was provided during service initialization.`);
    }
    return table;
  }

  async getKnowledgeUserState(category: string, concept: string): Promise<KnowledgeUserState | undefined> {
    const table: Table<KnowledgeUserState, string> = this.getTableByCategory(category);
    return table.get(concept);
  }

  async getAllKnowledgeUserStates(category: string): Promise<KnowledgeUserState[]> {
    const table: Table<KnowledgeUserState, string> = this.getTableByCategory(category);
    return table.toArray();
  }

  async getAllKnowledgeUserStateDict(category: string): Promise<{ [concept: string]: KnowledgeUserState }> {
    const table: Table<KnowledgeUserState, string> = this.getTableByCategory(category);
    const statesArray = await table.toArray();
    return statesArray.reduce((acc, state) => {
      acc[state.concept] = state;
      return acc;
    }, {} as { [concept: string]: KnowledgeUserState });
  }

  async addOrUpdateKnowledgeUserState<T>(category: T, state: KnowledgeUserState): Promise<string> {
    const table: Table<KnowledgeUserState, string> = this.getTableByCategory(category);
    return table.put(state);
  }

  async bulkAddOrUpdateKnowledgeUserStates<T>(category: T, states: KnowledgeUserState[]): Promise<string[]> {
    const table: Table<KnowledgeUserState, string> = this.getTableByCategory(category);
    await table.bulkPut(states);
    return states.map((s) => s.concept);
  }

  async deleteKnowledgeUserState(category: string, concept: string): Promise<void> {
    const table: Table<KnowledgeUserState, string> = this.getTableByCategory(category);
    return table.delete(concept);
  }

  /**
   * Clears all knowledge user state data for ALL categories.
   */
  async clearAllData(): Promise<void> {
    const clearPromises: Promise<void>[] = [];
    this.categoryTables.forEach((table) => {
      clearPromises.push(table.clear());
    });
    await Promise.all(clearPromises);
  }

  /**
   * Clears all knowledge user state data for a specific category.
   * @param category The category for which to clear data.
   */
  async clearDataForCategory(category: string): Promise<void> {
    const table = this.getTableByCategory(category);
    await table.clear();
  }

  async getOrCreateKnowledgeUserState(
    category: string,
    conceptId: string,
    concept: string,
    conceptType: string,
    defaultStatus: ConceptStatus = ConceptStatus.NotEncountered,
  ): Promise<KnowledgeUserState> {
    let userState = await this.getKnowledgeUserState(category, concept);
    if (!userState) {
      userState = {
        conceptId,
        concept,
        conceptType,
        conceptPath: '', // Default or derive if necessary
        status: defaultStatus,
        stats: {
          totalReviews: 0,
          successCount: 0,
          failedCount: 0,
          totalTimeSpent: 0,
          progress: 0,
        },
        firstTimeEncountered: defaultStatus !== ConceptStatus.NotEncountered ? new Date() : undefined,
      };
      await this.addOrUpdateKnowledgeUserState(category, userState);
    }
    return userState;
  }
}
